import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Collapse,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import {
  AssignmentLateOutlined,
  ChevronRightOutlined,
  EventBusyOutlined,
  Inventory,
  Inventory2Outlined,
  ListAltOutlined,
  RemoveCircleOutline,
} from '@mui/icons-material'

import DataViewTable from '../../../../../components/shared/DataViewTable/DataViewTable'
import IssueCard from '../../../../../components/inspector/shared/IssueCard'
import type { ActiveFilterChip } from '../../../../../components/inspector/shared/ActiveFilterChips'
import { useExceptionFilterValues } from '../../../../../hooks/useExceptionFilterValues'
import { dormantConfig } from '../../../../../config/tools/productCatalogueReview/dormantConfig'
import { fmtInt } from '../../../../../config/tools/productCatalogueReview/formatters'
import { AGING_LABELS } from '../../../../../config/tools/productCatalogueReview/renderers'
import type {
  DormantItem,
  DormantPayload,
} from '../../../../../config/tools/productCatalogueReview/types'
import {
  NO_CATEGORY_LABEL,
  NO_CATEGORY_VALUE,
} from '../../../../../utils/productCategory'
import { poolwerxColors } from '../../../../../theme/colors'
import CatalogueFilterPanel from './CatalogueFilterPanel'

export interface DormantFilterSnapshot {
  focus: FocusKey
  search: string
  categories: string[]
  productTypes: string[]
  countCompositeAsActivity: boolean
}

interface Props {
  data: DormantPayload
  fpCode?: string
  months?: number
  title?: string
  /**
   * Called whenever the tab's active filter set changes. The page reads
   * the latest snapshot at export time to scope the Dormant export sheet
   * to what the user sees on screen.
   */
  onSnapshotChange?: (snapshot: DormantFilterSnapshot) => void
}

const PRODUCT_TYPE_LABELS: Record<string, string> = {
  simple: 'Standard',
  composite_kit: 'Composite (kit)',
  composite_dosage: 'Composite (dosage)',
  variant_parent: 'Variant (parent)',
  variant_child: 'Variant (child)',
}

export type FocusKey =
  | 'needs_review'
  | 'stock_on_hand'
  | 'no_stock'
  | 'negative'
  | 'never_sold'
  | 'all'

interface FocusDef {
  key: FocusKey
  label: string
  icon: React.ReactNode
  predicate: (item: DormantItem) => boolean
}

const FOCUS_DEFS: FocusDef[] = [
  {
    key: 'needs_review',
    label: 'Needs review',
    icon: <AssignmentLateOutlined />,
    // Stock on hand OR negative stock - dormant products that likely
    // need action, the FP default workflow.
    predicate: (item) => Math.round(item.on_hand_units) !== 0,
  },
  {
    key: 'stock_on_hand',
    label: 'Stock on hand',
    icon: <Inventory2Outlined />,
    predicate: (item) => Math.round(item.on_hand_units) > 0,
  },
  {
    key: 'no_stock',
    label: 'No stock',
    icon: <Inventory />,
    predicate: (item) => Math.round(item.on_hand_units) === 0,
  },
  {
    key: 'negative',
    label: 'Negative stock',
    icon: <RemoveCircleOutline />,
    predicate: (item) => Math.round(item.on_hand_units) < 0,
  },
  {
    key: 'never_sold',
    label: 'Never sold',
    icon: <EventBusyOutlined />,
    predicate: (item) => item.aging === 'never',
  },
  {
    key: 'all',
    label: 'All dormant',
    icon: <ListAltOutlined />,
    predicate: () => true,
  },
]

const SECTION_LABEL_SX = {
  color: 'text.secondary',
  letterSpacing: '0.5px',
  fontWeight: 600,
  fontSize: '0.7rem',
  textTransform: 'uppercase' as const,
  lineHeight: 1.4,
  mb: 0.75,
}

const periodLabel = (months?: number): string => {
  if (!months) return ''
  if (months === 1) return 'last 30 days'
  return `last ${months} months`
}

const DormantTab: React.FC<Props> = ({
  data,
  fpCode,
  months,
  title,
  onSnapshotChange,
}) => {
  const [showAging, setShowAging] = useState(false)
  const [focusKey, setFocusKey] = useState<FocusKey>('needs_review')

  const {
    filterValues,
    updateFilterValue,
    resetFilters: resetConfigFilters,
  } = useExceptionFilterValues(dormantConfig)

  useEffect(() => {
    if (!onSnapshotChange) return
    onSnapshotChange({
      focus: focusKey,
      search: (filterValues.search ?? '').toString(),
      categories: (filterValues.category_root_name ?? []) as string[],
      productTypes: (filterValues.product_type ?? []) as string[],
      countCompositeAsActivity:
        filterValues.count_composite_as_activity !== false,
    })
  }, [focusKey, filterValues, onSnapshotChange])

  // Apply non-FOCUS filters once. Counts shown on the FOCUS tiles read
  // from this post-filter set so they stay accurate as the user narrows
  // by search/category/type/composite toggle - but stable across FOCUS
  // selections (FOCUS is applied last).
  // `active: true` is injected so the shared product-name meta renderer
  // doesn't flag these dormant (active) rows as Inactive.
  const baseFiltered = useMemo(() => {
    const search = (filterValues.search ?? '').toString().toLowerCase().trim()
    const categories = (filterValues.category_root_name ?? []) as string[]
    const types = (filterValues.product_type ?? []) as string[]
    const countCompositeAsActivity =
      filterValues.count_composite_as_activity ?? true

    return data.items.filter((item) => {
      if (search) {
        const matchesSearch =
          (item.name?.toLowerCase().includes(search) ?? false) ||
          (item.sku?.toLowerCase().includes(search) ?? false)
        if (!matchesSearch) return false
      }
      if (categories.length > 0) {
        const raw = item.category_root_name
        const key =
          raw == null || raw === '' || raw === 'No category'
            ? NO_CATEGORY_VALUE
            : String(raw)
        if (!categories.includes(key)) return false
      }
      if (types.length > 0 && !types.includes(item.product_type)) return false
      if (countCompositeAsActivity && item.composite_active) return false
      return true
    })
  }, [data.items, filterValues])

  const focusCounts = useMemo(() => {
    const counts: Record<FocusKey, number> = {
      needs_review: 0,
      stock_on_hand: 0,
      no_stock: 0,
      negative: 0,
      never_sold: 0,
      all: baseFiltered.length,
    }
    for (const item of baseFiltered) {
      for (const def of FOCUS_DEFS) {
        if (def.key === 'all') continue
        if (def.predicate(item)) counts[def.key] += 1
      }
    }
    return counts
  }, [baseFiltered])

  const activeFocus =
    FOCUS_DEFS.find((d) => d.key === focusKey) ?? FOCUS_DEFS[0]

  const filteredItems = useMemo(
    () =>
      baseFiltered
        .filter(activeFocus.predicate)
        .map((item) => ({ ...item, active: true })),
    [baseFiltered, activeFocus],
  )

  const configFiltersActive = useMemo(() => {
    for (const f of dormantConfig.filters ?? []) {
      const v = filterValues[f.key]
      if (f.type === 'multiselect' && Array.isArray(v) && v.length > 0) return true
      if (f.type === 'toggle' && !!v !== !!f.defaultEnabled) return true
      if (f.type === 'search' && typeof v === 'string' && v.trim() !== '')
        return true
    }
    return false
  }, [filterValues])

  const focusActive = focusKey !== 'needs_review'
  const hasAnyActiveFilter = focusActive || configFiltersActive

  const onReset = () => {
    setFocusKey('needs_review')
    resetConfigFilters()
  }

  const activeFilterChips: ActiveFilterChip[] = useMemo(() => {
    const chips: ActiveFilterChip[] = []
    if (focusActive) {
      chips.push({
        key: 'focus',
        kind: 'filter',
        label: `Focus: ${activeFocus.label}`,
        onRemove: () => setFocusKey('needs_review'),
      })
    }
    const search = (filterValues.search ?? '') as string
    if (search.trim() !== '') {
      chips.push({
        key: 'search',
        kind: 'filter',
        label: `Search: ${search.trim()}`,
        onRemove: () => updateFilterValue('search', ''),
      })
    }
    const categories = (filterValues.category_root_name ?? []) as string[]
    for (const cat of categories) {
      chips.push({
        key: `cat:${cat}`,
        kind: 'filter',
        label: cat === NO_CATEGORY_VALUE ? NO_CATEGORY_LABEL : cat,
        onRemove: () =>
          updateFilterValue(
            'category_root_name',
            categories.filter((c) => c !== cat),
          ),
      })
    }
    const types = (filterValues.product_type ?? []) as string[]
    for (const t of types) {
      chips.push({
        key: `type:${t}`,
        kind: 'filter',
        label: PRODUCT_TYPE_LABELS[t] ?? t,
        onRemove: () =>
          updateFilterValue(
            'product_type',
            types.filter((x) => x !== t),
          ),
      })
    }
    // Toggle is default-on; surface a chip only when the user has
    // explicitly switched it off.
    if (filterValues.count_composite_as_activity === false) {
      chips.push({
        key: 'count_composite_as_activity',
        kind: 'filter',
        label: 'Including composite-active',
        onRemove: () => updateFilterValue('count_composite_as_activity', true),
      })
    }
    return chips
  }, [focusActive, activeFocus, filterValues, updateFilterValue])

  const max = Math.max(1, ...data.aging.map((a) => a.count))

  const focusStrip = (
    <Box>
      <Typography sx={SECTION_LABEL_SX}>Focus</Typography>
      <Stack
        direction="row"
        alignItems="stretch"
        sx={{ flexWrap: 'wrap', gap: 1 }}
      >
        {FOCUS_DEFS.map((def) => (
          <IssueCard
            key={def.key}
            label={def.label}
            count={focusCounts[def.key]}
            active={focusKey === def.key}
            icon={def.icon}
            onClick={() => setFocusKey(def.key)}
          />
        ))}
      </Stack>
    </Box>
  )

  const periodContext = months ? (
    <Typography
      variant="caption"
      sx={{ color: 'text.secondary', alignSelf: 'center', whiteSpace: 'nowrap' }}
    >
      Dormant relative to {periodLabel(months)}
    </Typography>
  ) : null

  const agingCollapsible = (
    <Box>
      <Button
        size="small"
        onClick={() => setShowAging((p) => !p)}
        startIcon={
          <ChevronRightOutlined
            sx={{
              transform: showAging ? 'rotate(90deg)' : 'none',
              transition: 'transform 150ms',
              fontSize: 18,
            }}
          />
        }
        sx={{
          textTransform: 'none',
          color: 'text.secondary',
          fontSize: '0.75rem',
          fontWeight: 500,
          px: 0.5,
          minHeight: 28,
        }}
      >
        Aging distribution
      </Button>
      <Collapse in={showAging} unmountOnExit>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mt: 1,
            borderColor: 'rgba(0, 0, 0, 0.08)',
            bgcolor: poolwerxColors.grey[50],
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            Aging distribution (active products with no sales in period)
          </Typography>
          <Stack spacing={0.75} sx={{ mt: 1.5 }}>
            {data.aging.map(({ bucket, count }) => {
              const pct = (count / max) * 100
              const barColor =
                bucket === 'never' || bucket === 'over_24m'
                  ? 'error.light'
                  : bucket === '12-24m'
                    ? 'warning.light'
                    : 'primary.light'
              return (
                <Stack
                  key={bucket}
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ minHeight: 22 }}
                >
                  <Box sx={{ width: 110, flexShrink: 0 }}>
                    <Typography variant="body2">{AGING_LABELS[bucket]}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      sx={{
                        height: 12,
                        borderRadius: 1,
                        backgroundColor: 'grey.100',
                        '& .MuiLinearProgress-bar': { backgroundColor: barColor },
                      }}
                    />
                  </Box>
                  <Box sx={{ width: 60, textAlign: 'right', flexShrink: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {fmtInt(count)}
                    </Typography>
                  </Box>
                </Stack>
              )
            })}
          </Stack>
        </Paper>
      </Collapse>
    </Box>
  )

  return (
    <Stack spacing={0}>
      <CatalogueFilterPanel
        title="Filter products"
        helperText="Narrow results by focus, search, category, type, or composite usage."
        onReset={onReset}
        hasAnyActiveFilter={hasAnyActiveFilter}
        primaryChips={focusStrip}
        filters={dormantConfig.filters ?? []}
        filterValues={filterValues}
        updateFilterValue={updateFilterValue}
        filterRowSuffix={periodContext}
        secondaryCollapsible={agingCollapsible}
        rows={data.items}
      />
      <DataViewTable
        config={dormantConfig}
        data={filteredItems}
        variant="inspector"
        fpCode={fpCode}
        embedded
        filterValues={filterValues}
        updateFilterValue={updateFilterValue}
        activeFilterChips={activeFilterChips}
        title={title}
      />
    </Stack>
  )
}

export default DormantTab
